import { FormControl, InputLabel, Paper, TextField, useTheme } from '@mui/material';
import { useState, useContext, useMemo, useCallback } from 'react';
import Popover from 'src/components/common/popover/Popover';
import MultipleNumberInput from 'src/components/common/MultipleNumberInput/MultipleNumberInput';
import { useLocale } from '@repo/i18n';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { zIndex } from 'src/utils/widget/zIndex';
import { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';

export type QuantityPickerPopoverProps = {
    items: QuantityInputItem[];
    onChange: (id: string, value: number) => void;
    labelColor?: string;
    variant?: 'standard' | 'outlined' | 'filled';
    color?: 'primary' | 'secondary';
    labelText?: string;
};

export function QuantityPickerPopover(props: QuantityPickerPopoverProps) {
    const {
        items,
        onChange,
        labelColor,
        variant = 'outlined',
        color = 'primary',
        labelText,
    } = props;
    const theme = useTheme();
    const mountPoint = useContext(MountPointContext);
    const { t } = useLocale();
    const [multipleNumberInputRef, setMultipleNumberInputRef] = useState<HTMLInputElement | null>(
        null,
    );

    const noTravelers = useMemo(() => items.every((item) => item.value === 0), [items]);

    const handleTextFieldClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
        setMultipleNumberInputRef(e.currentTarget);
    }, []);

    return (
        <>
            <FormControl fullWidth={true}>
                <InputLabel
                    id="quantity-picker-popover-label"
                    sx={{
                        ...TextFieldStyleUtils.positionInputLabelAboveField,
                        color: labelColor ? `${labelColor} !important` : undefined,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {labelText ?? t.how_many_are_going}
                </InputLabel>
                <TextField
                    sx={{ position: 'relative' }}
                    variant={variant}
                    color={color}
                    margin="dense"
                    size="small"
                    onClick={handleTextFieldClick}
                    inputProps={{
                        'aria-labelledby': 'quantity-picker-popover-label',
                    }}
                    value={
                        noTravelers
                            ? t.select_travelers
                            : items.map((item) => `${item.value} ${item.label}`).join(', ')
                    }
                />
            </FormControl>
            <Popover
                open={Boolean(multipleNumberInputRef)}
                anchorEl={multipleNumberInputRef}
                sx={{
                    '&&': {
                        zIndex: zIndex.alwaysVisible,
                    },
                    mt: theme.spacing(1),

                    '& > .MuiPaper-root': {
                        width: Math.max(
                            multipleNumberInputRef?.getBoundingClientRect().width ?? 0,
                            270,
                        ),
                    },
                }}
                container={mountPoint.popover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setMultipleNumberInputRef(null)}
                aria-modal={true}
                disableEnforceFocus={true}
            >
                <Paper sx={{ padding: 1 }}>
                    <MultipleNumberInput
                        items={items}
                        onChange={onChange}
                        labelText=""
                        color={theme.palette.common.black}
                        backgroundColor={theme.palette.common.white}
                    />
                </Paper>
            </Popover>
        </>
    );
}
